import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import config from 'config'
import useUser from 'hooks/useUser'

import { DealerRedirectorInterface } from './types'

/**
 * Determines if the current location is on a specified url.
 * @param url The url to check.
 * @returns True if on url, false otherwise.
 */
function notOnLocation(url: string) {
    return window.location.origin !== url
}
/**
 * Redirect to url.
 * @param url The url to redirect to.
 */
function redirectToLocation(url: string) {
    window.location.replace(url)
}

/**
 * Determine if redirect should be executed.
 * @param url The request URL.
 * @returns {boolean | undefined} True or false depending on if a redirect should be made.
 *          Returns undefined if state could not be determined.
 */
export function shouldDoRedirect(
    url: string | undefined,
    isLender?: boolean,
): boolean | undefined {
    if (config.ENVIRONMENT === 'e2e' || config.ENVIRONMENT === 'qa') {
        // Branding not supported yet
        return false
    }

    if (isLender) {
        return false
    }

    if (!url) {
        return undefined
    }

    return notOnLocation(url)
}

export default function DealerRedirector({
    onRedirectStateChange,
}: DealerRedirectorInterface) {
    const { user } = useUser()
    const location = useLocation()

    useEffect(() => {
        const checkAndRedirect = async () => {
            onRedirectStateChange(undefined)

            const shouldRedirect = shouldDoRedirect(
                user?.dealerPortalUrl,
                user?.isLender,
            )

            if (shouldRedirect === undefined) {
                // User is loaded but has no dealerPortalUrl — no redirect needed
                if (user !== undefined) {
                    onRedirectStateChange(false)
                }

                return
            }

            if (shouldRedirect) {
                onRedirectStateChange(true)

                // Use redirectTo path directly or fallback to current hash
                const redirectTo = location.state?.redirectTo

                const targetHash = redirectTo
                    ? `#${redirectTo}`
                    : globalThis.location.hash

                redirectToLocation(`${user?.dealerPortalUrl}${targetHash}`)

                return
            }

            onRedirectStateChange(false)
        }

        checkAndRedirect()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.dealerPortalUrl, user?.isLender, user !== undefined])

    return null
}
